import {
  Box, Container, Typography, Grid, Card, CardContent, CardMedia,
  CardActionArea, Chip, CircularProgress, Button, Pagination, useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  useGetPublishedPostsQuery, useGetPostsByCategoryQuery,
  useGetPublicCategoriesQuery, useGetWebsiteSettingsQuery,
} from "../../../../../app/websiteApiSlice";
import { useGetPublicSchoolInfoQuery } from "../../../../../app/authApiSlice";
import WebsiteHeader from "../../WebsiteHeader";
import WebsiteFooter from "../../WebsiteFooter";
import SeoHead from "../../SeoHead";
import "./bold.css";

export default function BoldPostsList({ settings: settingsProp, categorySlug, page, onPageChange }) {
  const { data: school } = useGetPublicSchoolInfoQuery();
  const { data: settings } = useGetWebsiteSettingsQuery(undefined, { skip: !!settingsProp });
  const activeSettings = settingsProp || settings;
  const { data: categories = [] } = useGetPublicCategoriesQuery();
  const muiTheme = useTheme();
  const accent = muiTheme.palette.primary.main;

  const { data: allPosts, isLoading: loadingAll } = useGetPublishedPostsQuery({ page, per_page: 9 }, { skip: !!categorySlug });
  const { data: catData, isLoading: loadingCat } = useGetPostsByCategoryQuery({ slug: categorySlug, page, per_page: 9 }, { skip: !categorySlug });

  const isLoading = categorySlug ? loadingCat : loadingAll;
  const data = categorySlug ? catData : allPosts;
  const posts = data?.posts || [];
  const totalPages = data?.pages || 1;
  const activeCategory = categories.find((c) => c.slug === categorySlug);

  return (
    <Box className="website-bold" sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#0f0f0f" }}>
      <SeoHead title={activeCategory ? `${activeCategory.name} — Blog` : "Blog"} schoolName={school?.name} />
      <WebsiteHeader school={school} navItems={activeSettings?.nav_items} variant="bold" />

      {/* Category filter bar */}
      <Box sx={{ bgcolor: "#1a1a1a", py: 1.5, overflowX: "auto" }}>
        <Container maxWidth="lg">
          <Box display="flex" gap={1}>
            <Chip
              label="All"
              component={Link} to="/blog" clickable
              sx={{
                color: !categorySlug ? "white" : "grey.500",
                bgcolor: !categorySlug ? accent : "transparent",
                border: "1px solid",
                borderColor: !categorySlug ? accent : "grey.700",
              }}
            />
            {categories.map((c) => (
              <Chip
                key={c.id}
                label={c.name}
                component={Link}
                to={`/blog/category/${c.slug}`}
                clickable
                sx={{
                  color: categorySlug === c.slug ? "white" : "grey.500",
                  bgcolor: categorySlug === c.slug ? accent : "transparent",
                  border: "1px solid",
                  borderColor: categorySlug === c.slug ? accent : "grey.700",
                }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6, flex: 1 }}>
        <Typography variant="h4" fontWeight={900} sx={{ color: "white", mb: 4 }}>
          {activeCategory ? activeCategory.name : "All Stories"}
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: accent }} /></Box>
        ) : posts.length === 0 ? (
          <Typography sx={{ color: "grey.500" }}>No posts yet.</Typography>
        ) : (
          <>
            <Grid container spacing={2} mb={4}>
              {posts.map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post.id}>
                  <Card sx={{ borderRadius: 2, bgcolor: "#1a1a1a", height: "100%", "&:hover": { transform: "translateY(-4px)", transition: "0.2s" } }}>
                    <CardActionArea component={Link} to={`/blog/${post.slug}`}>
                      {post.featured_image && (
                        <CardMedia component="img" height="180" image={post.featured_image} alt={post.title} />
                      )}
                      <CardContent>
                        <Box display="flex" gap={0.5} mb={1}>
                          {post.categories?.slice(0, 2).map((c) => (
                            <Chip key={c.id} label={c.name} size="small" sx={{ bgcolor: accent, color: "white", fontSize: "0.65rem" }} />
                          ))}
                        </Box>
                        <Typography variant="subtitle1" fontWeight={800} sx={{ color: "white" }}>{post.title}</Typography>
                        {post.published_at && (
                          <Typography variant="caption" sx={{ color: "grey.500" }}>
                            {format(new Date(post.published_at), "MMM d, yyyy")}
                          </Typography>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center">
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_e, v) => onPageChange(v)}
                  sx={{ "& .MuiPaginationItem-root": { color: "white" } }}
                />
              </Box>
            )}
          </>
        )}
      </Container>

      <WebsiteFooter settings={activeSettings} school={school} variant="bold" />
    </Box>
  );
}
